import { useId, useRef, useState, type FormEvent } from "react";
import { RotateCcw } from "lucide-react";
import type { HudSnapshot } from "@/game/sim";
import type { SubmitResult } from "@/lib/leaderboards";
import {
  BOARD_SIZE,
  PRIVACY_URL,
  cleanInitials,
  loadInitials,
  mintSubmissionId,
  postedLine,
  saveInitials,
  submissionFor,
} from "@/game/board";
import { submitRun, useBoard } from "@/game/leaderboards";
import { sfxPosted } from "@/game/audio";
import { useProof } from "@/game/turnstile";
import { Board } from "./Board";

function formatTime(t: number): string {
  const n = Number.isFinite(t) && t > 0 ? t : 0;
  const m = Math.floor(n / 60);
  const s = n - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, "0")}`;
}

export function ResultsCard({ hud, onAgain }: { hud: HudSnapshot; onAgain: () => void }) {
  const { board, reload, replace } = useBoard(BOARD_SIZE);
  const [initials, setInitials] = useState(loadInitials);
  const [posting, setPosting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  // Minted when the card mounts, before anything is posted: it is the outbox
  // key and the value Turnstile binds the token to, so the widget can render
  // against it straight away.
  const [submissionId] = useState(mintSubmissionId);
  const proofRef = useRef<HTMLDivElement>(null);
  const initialsId = useId();
  const noteId = useId();
  useProof(proofRef, result ? null : submissionId);

  const food = hud.hotDogs + hud.burgers;
  const bonus = hud.time * 1000 - hud.adjustedMs;

  const post = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const submit = async () => {
    if (posting || result || initials.length === 0) return;
    setPosting(true);
    saveInitials(initials);
    const run = submissionFor({
      finishSeconds: hud.time,
      hotDogs: hud.hotDogs,
      burgers: hud.burgers,
      seed: hud.seed,
    });
    const outcome = await submitRun({ initials, ...run }, submissionId);
    setPosting(false);
    setResult(outcome);
    if (outcome.status !== "accepted") return;
    sfxPosted();
    if (board) replace(outcome.board);
    else reload();
  };

  return (
    <div className="absolute inset-0 z-10 flex items-end justify-center overflow-y-auto bg-overlay px-6 pb-16 pt-[max(3rem,env(safe-area-inset-top))] sm:items-center sm:pb-0">
      <div className="w-full max-w-md rounded-xl bg-pine-mid p-6 shadow-panel sm:p-8">
        <p className="text-xs font-medium uppercase tracking-[0.24em] text-ice-bright">Finish</p>
        <p className="mt-3 font-mono text-5xl font-semibold tabular-nums tracking-tight text-snow">
          {formatTime(hud.adjustedMs / 1000)}
        </p>
        <p className="mt-3 text-sm text-snow-dim">
          Clock {formatTime(hud.time)} · Food −{formatTime(bonus / 1000)}
        </p>
        <p className="mt-1 text-sm text-snow-dim">
          Best {formatTime(hud.best / 1000)}
          {hud.isBest ? " · New best" : ""}
        </p>
        <p className="mt-1 text-sm text-snow-dim">
          Food {food} · {hud.hotDogs} dog{hud.hotDogs === 1 ? "" : "s"} · {hud.burgers} burger
          {hud.burgers === 1 ? "" : "s"}
        </p>

        {!result && (
          <form onSubmit={post} className="mt-6">
            <label
              htmlFor={initialsId}
              className="block text-xs font-medium uppercase tracking-[0.18em] text-ice"
            >
              Sign the board
            </label>
            <div className="mt-2 flex gap-2">
              <input
                id={initialsId}
                name="initials"
                type="text"
                value={initials}
                onChange={(e) => setInitials(cleanInitials(e.target.value))}
                maxLength={3}
                minLength={1}
                autoCapitalize="characters"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="send"
                aria-describedby={noteId}
                disabled={posting}
                className="h-12 w-28 rounded-lg border border-ice/40 bg-pine-deep px-3 text-center font-mono text-2xl font-semibold uppercase tracking-[0.3em] text-snow outline-none focus:border-ice-bright focus-visible:ring-2 focus-visible:ring-ice-bright/40 disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={posting || initials.length === 0}
                className="min-h-12 flex-1 rounded-lg bg-snow px-4 text-base font-semibold text-pine-deep transition-transform duration-150 hover:bg-ice-bright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-bright active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {posting ? "Posting…" : "Post to the board"}
              </button>
            </div>
            {/* The proof widget mounts here. It stays empty until Cloudflare
                needs a checkbox, then a 300x65 widget has to fit above the
                post button, unclipped -- so no fixed height, no overflow. */}
            <div ref={proofRef} className="flex w-full min-w-0 justify-center [&>iframe]:mt-3" />
            <p id={noteId} className="mt-2 text-xs text-snow-dim">
              Up to three letters or numbers, shown on the arcade board.{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-snow-dim/40 underline-offset-4 hover:text-snow"
              >
                Privacy
              </a>
            </p>
          </form>
        )}

        <p aria-live="polite" className={result ? "mt-4 text-sm text-snow" : "sr-only"}>
          {result ? postedLine(result) : ""}
        </p>

        <Board
          board={board}
          mark={result?.status === "accepted" ? result.entry : null}
          className="mt-5"
        />

        <button
          type="button"
          onClick={onAgain}
          className="mt-6 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-ice/40 px-6 py-3 text-base font-semibold text-snow transition-transform duration-150 hover:bg-pine focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ice-bright active:scale-[0.98]"
        >
          <RotateCcw className="size-4" strokeWidth={2} />
          Ride again
        </button>
      </div>
    </div>
  );
}
